/**
 * Visit Detail Drawer Shell (Phase 4 Vague 3a)
 *
 * ESM replacement for the 1031-LOC inline <script> that used to live in
 * ``templates/office/components/_visit_drawer.html``.
 *
 * The drawer is a single right-sidebar slide-in shell whose
 * ``#visitDetailDrawerContent`` is repeatedly replaced with one of
 * eight dynamically-loaded partials:
 *
 *   - showVisitDrawer(id)         → /office/visits/{id}/drawer/                (read-only details)
 *   - showRescheduleDrawer(id)    → /office/visits/{id}/reschedule/drawer/     (form)
 *   - showMarkDoneDrawer(id)      → /office/visits/{id}/mark-done/drawer/      (form)
 *   - showNotPerformedDrawer(id)  → /office/visits/{id}/not-performed/drawer/  (form)
 *   - showNoShowDrawer(id)        → /office/visits/{id}/no-show/drawer/        (form)
 *   - showCancelDrawer(id)        → /office/visits/{id}/cancel/drawer/         (form)
 *   - showCleanerAbsentDrawer(id) → /office/visits/{id}/cleaner-absent/drawer/ (form)
 *
 * Visit *creation* is no longer handled here: the office now uses the
 * shared customer-first quick-create drawer (``window.ScheduleHub.
 * quickCreate``, see ``schedule-quick-create.js``) on both the schedule
 * boards and the visits list.
 *
 * Auto-init lives on the drawer root via ``data-auto-init="visitDrawer"``.
 * Translation strings + URL templates flow from ``data-*`` attributes
 * on that root so the partial template stays inline-JS-free for the
 * shell itself (the per-action partials still have a small bootstrap
 * <script> after Vague 2 — Vague 3b will switch them to auto-init too).
 *
 * Public API (preserved from the legacy inline script):
 *   - window.showVisitDrawer / showRescheduleDrawer / showMarkDoneDrawer
 *     / showNotPerformedDrawer / showNoShowDrawer / showCancelDrawer
 *     / showCleanerAbsentDrawer
 *   - window.hideVisitDrawer
 *
 * The 6 visit-action partials migrated in Vague 2 still call
 * ``window.hideVisitDrawer`` from their footer / success handlers, so
 * preserving the names is non-negotiable.
 */

const SPINNER_HTML = `
    <div class="flex items-center justify-center py-16">
        <svg class="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
    </div>
`;

// Module-level state. Re-bound on every autoInitVisitDrawer pass —
// the drawer DOM is replaced when HTMX swaps #main-content (the shell
// is included on each consuming page via {% include %}, so the
// element identities change between navigations).
let drawerEl = null;
let backdropEl = null;
let contentEl = null;
let titleEl = null;
let badgesEl = null;
let translations = {};
let urlTemplates = {};

// Single document-level Escape listener for the SPA lifetime, in line
// with hx-boost-init.mdc. Tracked via module-scope flag so the listener
// is bound exactly once even though autoInitVisitDrawer is re-run on
// every htmx:afterSwap.
let escapeBound = false;

// Bespoke-drawer registry. Phase 7 modules register themselves on this
// shared global so wireBespokeDrawersInContent() can look them up by
// the ``data-bespoke-init="…"`` marker on the loaded partial. Pre-seeded
// here (instead of inside autoInit) so a Phase 7 module loaded before
// the visit drawer shell still finds the registry to populate.
if (typeof window !== 'undefined' && !window.visitDrawerInits) {
    window.visitDrawerInits = {};
}

/**
 * Re-execute <script> tags inside a freshly-injected HTML fragment.
 *
 * Browsers do not run ``<script>`` content set via ``innerHTML``; we
 * clone each script into a fresh DOM node and replace the original
 * (matching the legacy ``executeScriptsInElement`` behaviour: the
 * cleaner/job drawer code in ``utils/ui/dynamic-drawer.js`` uses a
 * different "append to body + setTimeout remove" variant for the
 * read-only drawers, but the visit drawer shell historically did
 * in-place replacement so we keep that contract).
 *
 * Vague 3b will replace this mechanism with auto-init walking for
 * the 6 visit-action partials so they can drop their own bootstrap
 * <script> tags. Detail partials (visits/_detail_drawer.html,
 * jobs/partials/_detail_drawer.html) still ship inline scripts and
 * will be migrated in Vague 3c.
 */
function executeScriptsInElement(element) {
    const scripts = element.querySelectorAll('script');
    scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
    });
}

/**
 * Apply "portal" content from a freshly-injected partial into elements
 * that live OUTSIDE ``#visitDetailDrawerContent`` — typically the
 * status-badge slot in the drawer header.
 *
 * Markup contract: the loaded partial includes one or more
 * ``[data-portal-target="<elementId>"]`` containers; for each one we
 * find the destination by id, replace its innerHTML with the source's
 * innerHTML, and remove the source from the content node.
 *
 * Phase 4 Vague 3c: replaces the inline ``<script>`` IIFE that used
 * to paint badges in ``templates/office/visits/partials/_detail_drawer.html``.
 */
function applyPortalsInContent(content) {
    content.querySelectorAll('[data-portal-target]').forEach((source) => {
        const targetId = source.dataset.portalTarget;
        if (!targetId) return;
        const target = document.getElementById(targetId);
        if (!target) return;
        target.innerHTML = source.innerHTML;
        source.remove();
    });
}

/**
 * Wire any ``[data-drawer-close]`` buttons inside freshly-injected
 * content (typically the footer "Go Back" / "Cancel" buttons of an
 * action partial) to ``hideVisitDrawer``. Idempotent via a per-element
 * JS-property flag so re-injecting the same partial doesn't double-bind.
 */
function wireCloseButtonsInContent(content) {
    content.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        if (btn._drawerCloseBound) return;
        btn._drawerCloseBound = true;
        btn.addEventListener('click', hideVisitDrawer);
    });
}

/**
 * Auto-init walker for declarative drawer action forms.
 *
 * Replaces the per-partial bootstrap ``<script>`` for the simple
 * factorable cases (cancel, no_show after Vague 3b). Walks the
 * freshly-injected content for ``[data-action-form]`` markers, reads
 * config from data-* attributes, and calls the canonical
 * ``wireDrawerActionForm`` helper exposed by
 * ``utils/forms/drawer-action-form.js``.
 *
 * Markup contract (the partial only writes HTML, no JS):
 *
 *   <form id="drawerCancelForm"
 *         data-action-form
 *         data-submit-btn-id="drawerCancelSubmit"
 *         data-success-message="Visit cancelled"
 *         data-error-message="Error cancelling visit"
 *         data-error-container="drawerCancelReasonError"
 *         data-expect-json="false">
 *     ...
 *     <textarea id="drawer_cancel_reason"
 *               data-required-field
 *               data-error-target="drawerCancelReasonError"
 *               data-error-message="Please enter a cancellation reason">
 *     </textarea>
 *     ...
 *   </form>
 *
 * Each ``[data-required-field]`` input is wired with
 * ``requiredFieldValidator``; validators are appended to the
 * ``wireDrawerActionForm`` config in document order.
 *
 * Partials with custom validators (reschedule, mark_done,
 * cleaner_absent, not_performed) are NOT migrated to this
 * declarative form — they keep their inline ``<script>`` because
 * their validation logic is bespoke and not factorable through
 * data-* attributes alone.
 */
function wireActionFormsInContent(content) {
    if (typeof window.wireDrawerActionForm !== 'function') return;
    if (typeof window.requiredFieldValidator !== 'function') return;

    content.querySelectorAll('[data-action-form]').forEach((form) => {
        const ds = form.dataset;
        const validators = [];
        form.querySelectorAll('[data-required-field]').forEach((input) => {
            validators.push(window.requiredFieldValidator({
                inputId: input.id,
                errorId: input.dataset.errorTarget,
                message: input.dataset.errorMessage,
            }));
        });
        window.wireDrawerActionForm({
            formId: form.id,
            submitBtnId: ds.submitBtnId,
            validators,
            successMessage: ds.successMessage || '',
            errorMessage: ds.errorMessage || '',
            errorContainerId: ds.errorContainer || null,
            expectJson: ds.expectJson === 'true',
        });
    });
}

/**
 * Walker for partials with bespoke widget logic that doesn't fit the
 * declarative ``[data-action-form]`` shape — Phase 7 covers the 4
 * visit-action drawers whose forms ship custom widgets (live duration
 * display, breaks management, all-day toggle, date+time→ISO sync,
 * drawer-scoped Datepicker boot, …).
 *
 * Each Phase 7 module imports its bespoke logic, then registers itself
 * on the shared ``window.visitDrawerInits`` registry under a stable
 * name (e.g. ``visitNotPerformed``). The matching partial declares the
 * marker on a wrapper that lives inside the freshly-loaded content:
 *
 *   <div data-bespoke-init="visitNotPerformed"
 *        data-not-performed-success="…"
 *        data-not-performed-error="…">
 *     <form id="drawerNotPerformedForm">…</form>
 *   </div>
 *
 * The walker scans the loaded content, looks each name up in the
 * registry and calls ``init(rootEl)`` (the wrapper element). Modules
 * read translations / ids from the wrapper's dataset rather than from
 * inline ``{% trans %}`` JS literals — that's what removes the
 * remaining inline ``<script>`` from each partial.
 *
 * Idempotent at the wrapper level: the walker tags each processed
 * wrapper with ``_bespokeInitDone = true`` so a second pass on the
 * same content node is a no-op.
 */
function wireBespokeDrawersInContent(content) {
    const registry = window.visitDrawerInits;
    if (!registry) return;
    content.querySelectorAll('[data-bespoke-init]').forEach((rootEl) => {
        if (rootEl._bespokeInitDone) return;
        const name = rootEl.dataset.bespokeInit;
        const init = registry[name];
        if (typeof init !== 'function') {
            console.warn(`wireBespokeDrawersInContent: no init registered for "${name}"`);
            return;
        }
        rootEl._bespokeInitDone = true;
        try {
            init(rootEl);
        } catch (err) {
            console.error(`Bespoke drawer init "${name}" threw:`, err);
        }
    });
}

function isOpen() {
    return drawerEl && !drawerEl.classList.contains('translate-x-full');
}

function openShell() {
    if (!drawerEl || !backdropEl || !contentEl) return false;
    backdropEl.classList.remove('hidden');
    drawerEl.classList.remove('translate-x-full');
    document.body.classList.add('overflow-hidden');
    return true;
}

function showSpinner(title) {
    if (badgesEl) badgesEl.innerHTML = '';
    if (titleEl && title) titleEl.textContent = title;
    contentEl.innerHTML = SPINNER_HTML;
}

function showLoadError(messageKey) {
    const message = translations[messageKey] || translations.errorOccurred || 'Error loading content';
    const closeText = translations.close || 'Close';
    contentEl.innerHTML = `
        <div class="text-center py-16 text-red-500">
            <p>${message}</p>
            <button type="button" data-drawer-close class="mt-4 btn-secondary">${closeText}</button>
        </div>
    `;
    // Wire the freshly-created close button — it lives inside content
    // so the shell-level [data-drawer-close] handler doesn't reach it.
    contentEl.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        if (btn._drawerCloseBound) return;
        btn._drawerCloseBound = true;
        btn.addEventListener('click', hideVisitDrawer);
    });
}

/**
 * Generic drawer-content loader. All 7 visit-action ``showXxxDrawer``
 * functions delegate here — they only differ by URL, header title
 * (when reset on open) and the error message key.
 *
 * @param {object} opts
 * @param {string}   opts.url
 * @param {string}   [opts.title]            — set into #drawerLabel on open.
 * @param {string}   [opts.errorKey]         — translations[key] used in the error UI.
 * @param {function} [opts.onContentLoaded]  — called with the content node after
 *                                              executeScripts. Used by new-visit and
 *                                              quick-create to re-init datepickers /
 *                                              Flowbite / job search.
 */
function loadIntoDrawer({ url, title = null, errorKey = 'errorOccurred', onContentLoaded = null }) {
    if (!openShell()) return;
    showSpinner(title);

    fetch(url, {
        cache: 'no-store',
        headers: { 'HX-Request': 'true' },
    })
        .then((response) => {
            if (!response.ok) throw new Error(`Drawer load failed (${response.status})`);
            return response.text();
        })
        .then((html) => {
            contentEl.innerHTML = html;
            // Bespoke partial scripts run first (e.g. mark_done's
            // duration ticker, cleaner_absent's all-day toggle, the
            // reschedule date sync). Then the auto-init walker wires
            // declarative [data-action-form] forms (cancel, no_show
            // after Vague 3b — they have no inline <script> at all).
            // Move [data-portal-target] containers out of content
            // and into their named destinations BEFORE executing
            // partial scripts — so any script that depends on the
            // badges being painted (none currently, but defensive)
            // sees the post-portal state.
            applyPortalsInContent(contentEl);
            executeScriptsInElement(contentEl);
            wireCloseButtonsInContent(contentEl);
            wireActionFormsInContent(contentEl);
            wireBespokeDrawersInContent(contentEl);
            // Phase 8 — mount any cleaner picker that just landed in the
            // drawer. The partial used to ship its own IIFE so this step
            // wasn't needed; the ESM module is opt-in and idempotent so
            // calling it on every load is safe (per-element guard).
            if (typeof window.wireCleanerPickersInContent === 'function') {
                window.wireCleanerPickersInContent(contentEl);
            }
            if (typeof window.initCustomSelects === 'function') {
                window.initCustomSelects(contentEl);
            }
            if (typeof onContentLoaded === 'function') {
                onContentLoaded(contentEl);
            }
        })
        .catch((err) => {
            console.error(`Visit drawer load error (${url}):`, err);
            showLoadError(errorKey);
        });
}

function hideVisitDrawer() {
    if (!drawerEl || !backdropEl) return;
    drawerEl.classList.add('translate-x-full');
    backdropEl.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
    if (badgesEl) badgesEl.innerHTML = '';
    if (titleEl && translations.visitDetails) {
        titleEl.textContent = translations.visitDetails;
    }
}

// === Show functions for the 7 visit-related actions ====================

function showVisitDrawer(occurrenceId) {
    loadIntoDrawer({
        url: urlTemplates.visit.replace('{id}', encodeURIComponent(occurrenceId)),
        // Detail partial sets its own title via inline <script> (the
        // status badge painter runs after fetch). We only clear it.
        errorKey: 'errorLoadingVisitDetails',
    });
}

function showRescheduleDrawer(occurrenceId) {
    loadIntoDrawer({
        url: urlTemplates.reschedule.replace('{id}', encodeURIComponent(occurrenceId)),
        title: translations.rescheduleVisit,
        errorKey: 'errorLoadingRescheduleForm',
    });
}

function showMarkDoneDrawer(occurrenceId) {
    loadIntoDrawer({
        url: urlTemplates.markDone.replace('{id}', encodeURIComponent(occurrenceId)),
        title: translations.markAsDone,
        errorKey: 'errorLoadingMarkDoneForm',
    });
}

function showNotPerformedDrawer(occurrenceId) {
    loadIntoDrawer({
        url: urlTemplates.notPerformed.replace('{id}', encodeURIComponent(occurrenceId)),
        title: translations.markNotPerformed,
        errorKey: 'errorLoadingNotPerformedForm',
    });
}

function showNoShowDrawer(occurrenceId) {
    loadIntoDrawer({
        url: urlTemplates.noShow.replace('{id}', encodeURIComponent(occurrenceId)),
        title: translations.markNoShow,
        errorKey: 'errorLoadingNoShowForm',
    });
}

function showCancelDrawer(occurrenceId) {
    loadIntoDrawer({
        url: urlTemplates.cancel.replace('{id}', encodeURIComponent(occurrenceId)),
        title: translations.cancelVisit,
        errorKey: 'errorLoadingCancelForm',
    });
}

function showCleanerAbsentDrawer(occurrenceId) {
    loadIntoDrawer({
        url: urlTemplates.cleanerAbsent.replace('{id}', encodeURIComponent(occurrenceId)),
        title: translations.cleanerAbsent,
        errorKey: 'errorLoadingCleanerAbsentForm',
    });
}

// === Auto-init wiring ==================================================

/**
 * Read the translations + URL templates from data-* attributes on the
 * drawer root. Defaults preserve legacy URL paths so the partial can
 * skip declaring them when the standard /office/visits/{id}/... layout
 * is in use (which it always is at the time of writing).
 */
function readConfigFromRoot(root) {
    const ds = root.dataset;
    translations = {
        visitDetails: ds.titleVisitDetails || 'Visit Details',
        rescheduleVisit: ds.titleReschedule || 'Reschedule Visit',
        markAsDone: ds.titleMarkDone || 'Mark as Done',
        markNoShow: ds.titleNoShow || 'Mark No-Show',
        cancelVisit: ds.titleCancel || 'Cancel Visit',
        cleanerAbsent: ds.titleCleanerAbsent || 'Cleaner Absent',
        markNotPerformed: ds.titleNotPerformed || 'Mark Not Performed',
        close: ds.textClose || 'Close',
        errorLoadingVisitDetails: ds.errorVisitDetails || 'Error loading visit details',
        errorLoadingRescheduleForm: ds.errorReschedule || 'Error loading reschedule form',
        errorLoadingMarkDoneForm: ds.errorMarkDone || 'Error loading mark done form',
        errorLoadingNoShowForm: ds.errorNoShow || 'Error loading no-show form',
        errorLoadingCancelForm: ds.errorCancel || 'Error loading cancel form',
        errorLoadingCleanerAbsentForm: ds.errorCleanerAbsent || 'Error loading cleaner absent form',
        errorLoadingNotPerformedForm: ds.errorNotPerformed || 'Error loading not-performed form',
        errorOccurred: ds.errorGeneric || 'An error occurred',
    };
    urlTemplates = {
        visit: ds.urlVisit || '/office/visits/{id}/drawer/',
        reschedule: ds.urlReschedule || '/office/visits/{id}/reschedule/drawer/',
        markDone: ds.urlMarkDone || '/office/visits/{id}/mark-done/drawer/',
        notPerformed: ds.urlNotPerformed || '/office/visits/{id}/not-performed/drawer/',
        noShow: ds.urlNoShow || '/office/visits/{id}/no-show/drawer/',
        cancel: ds.urlCancel || '/office/visits/{id}/cancel/drawer/',
        cleanerAbsent: ds.urlCleanerAbsent || '/office/visits/{id}/cleaner-absent/drawer/',
    };
}

/**
 * Auto-init entry point. Called by ``_runAutoInit`` (app.js) on every
 * htmx:afterSwap of #main-content and on htmx:historyRestore. The
 * drawer DOM is replaced when a new page swaps in, so all element
 * lookups + close handlers must rebind here.
 */
export function autoInitVisitDrawer() {
    const root = document.querySelector('[data-auto-init="visitDrawer"]');
    if (!root) return;

    drawerEl = document.getElementById('visitDetailDrawer');
    backdropEl = document.getElementById('visitDrawerBackdrop');
    contentEl = document.getElementById('visitDetailDrawerContent');
    titleEl = document.getElementById('drawerLabel');
    badgesEl = document.getElementById('drawerStatusBadges');

    if (!drawerEl || !backdropEl || !contentEl) {
        console.warn('autoInitVisitDrawer: missing drawer/backdrop/content element');
        return;
    }

    readConfigFromRoot(root);

    // Per-element close handlers: the X button in the header + clicks
    // on the backdrop. Bind flag is a plain JS property (not dataset.*)
    // to avoid serialisation into HTMX history snapshots — see
    // hx-boost-init.mdc § 3.
    drawerEl.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        if (btn._drawerCloseBound) return;
        btn._drawerCloseBound = true;
        btn.addEventListener('click', hideVisitDrawer);
    });
    if (!backdropEl._drawerCloseBound) {
        backdropEl._drawerCloseBound = true;
        backdropEl.addEventListener('click', hideVisitDrawer);
    }

    // Single Escape listener for the SPA lifetime.
    if (!escapeBound) {
        escapeBound = true;
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && isOpen()) hideVisitDrawer();
        });
    }

    // Public globals — preserved so existing call-sites in the 6
    // consuming pages, the schedule_calendar.js, the visit-action
    // partials' footers ("Go Back" buttons), etc. keep working.
    window.showVisitDrawer = showVisitDrawer;
    window.showRescheduleDrawer = showRescheduleDrawer;
    window.showMarkDoneDrawer = showMarkDoneDrawer;
    window.showNotPerformedDrawer = showNotPerformedDrawer;
    window.showNoShowDrawer = showNoShowDrawer;
    window.showCancelDrawer = showCancelDrawer;
    window.showCleanerAbsentDrawer = showCleanerAbsentDrawer;
    window.hideVisitDrawer = hideVisitDrawer;
}

export function initVisitDrawer() {
    autoInitVisitDrawer();
}
